import { useEffect, useState } from "react";
import { api, money } from "../lib/api";
import { StatCard, Card, PageHeader } from "../components/common";
import { useAuth } from "../context/AuthContext";
import {
  FileText, CurrencyDollar, Warning, ShieldCheck, Users, Receipt, Camera, TrendUp, Wallet, Bell, ArrowRight, PaperPlaneTilt,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { FinancialCharts } from "../components/FinancialCharts";

const printReport = (stats, alerts, userName) => {
  const fmt = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(n || 0);
  const al = (alerts || []).map((a) => `<li>${a.message}</li>`).join("") || "<li>Sin alertas</li>";
  const html = `<html><head><title>Reporte ejecutivo de obra</title><style>body{font-family:Arial,sans-serif;padding:32px;color:#0F172A}h1{margin:0}h2{border-bottom:2px solid #FF5A00;padding-bottom:4px;margin-top:24px}
    .grid{display:flex;flex-wrap:wrap;gap:20px;margin-top:12px}.grid div b{display:block;font-size:20px}</style></head><body>
    <h1>Reporte ejecutivo de obra</h1><p style="color:#64748B">${new Date().toLocaleDateString("es-MX")} · ${userName || ""}</p>
    <h2>Indicadores</h2><div class="grid">
      <div>Contratos<b>${stats.contracts}</b></div>
      <div>Contratado<b>${fmt(stats.total_contracted)}</b></div>
      <div>Pagado<b>${fmt(stats.total_paid)}</b></div>
      <div>Por pagar<b>${stats.to_pay || 0}</b></div>
      <div>Estimaciones pendientes<b>${stats.pending_estimations}</b></div>
      <div>Sobre-estimaciones<b>${stats.over_estimations}</b></div>
      <div>Fotos de avance<b>${stats.photos}</b></div>
    </div>
    <h2>Alertas / pendientes</h2><ul>${al}</ul>
    </body></html>`;
  const w = window.open("", "_blank");
  w.document.write(html); w.document.close();
  setTimeout(() => { try { w.print(); } catch (e) {} }, 400);
};

const SEV = {
  high: { dot: "bg-danger", text: "text-danger", bg: "hover:bg-red-50" },
  medium: { dot: "bg-amber-500", text: "text-amber-700", bg: "hover:bg-amber-50" },
  low: { dot: "bg-slate-400", text: "text-slate-600", bg: "hover:bg-muted" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [alerts, setAlerts] = useState([]);

  useEffect(() => {
    api.get("/dashboard").then((r) => setStats(r.data));
    api.get("/alerts").then((r) => setAlerts(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <PageHeader overline={`Bienvenido, ${user?.name}`} title="Panel de obra"
        action={stats && ["admin", "supervisor", "gerente_general", "gerente_administrativo"].includes(user?.role) && (
          <button onClick={() => printReport(stats, alerts, user?.name)} data-testid="exec-report-btn"
            className="inline-flex items-center gap-2 h-11 px-4 border border-input rounded-sm font-medium text-sm hover:bg-muted transition-colors duration-200">
            <FileText size={18} weight="bold" /> Reporte ejecutivo
          </button>
        )} />

      {!stats ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Card key={i} className="p-6 h-28 animate-pulse bg-muted" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Contratos" value={stats.contracts} icon={FileText} />
            <StatCard label="Monto contratado" value={money(stats.total_contracted)} icon={CurrencyDollar} tone="orange" />
            <StatCard label="Pagado" value={money(stats.total_paid)} icon={TrendUp} tone="green" />
            <StatCard label="Sobre-estimaciones" value={stats.over_estimations} icon={Warning} tone={stats.over_estimations ? "red" : "default"} />
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
            <StatCard label="Contratistas" value={stats.contractors} icon={Users} />
            <StatCard label="Estimaciones pendientes" value={stats.pending_estimations} icon={Receipt} tone={stats.pending_estimations ? "orange" : "default"} />
            <StatCard label="Por pagar" value={stats.to_pay || 0} icon={Wallet} tone={stats.to_pay ? "orange" : "default"} />
            <StatCard label="Fotos de avance" value={stats.photos} icon={Camera} />
          </div>

          <FinancialCharts />

          <Card className="mt-6 p-6">
            <div className="flex items-center gap-2 mb-4">
              <Bell size={20} weight="fill" className="text-primary" />
              <div className="font-heading font-bold text-xl">Alertas</div>
              {alerts.length > 0 && <span className="ml-1 text-xs font-bold text-white bg-danger rounded-full px-2 py-0.5" data-testid="alerts-count">{alerts.length}</span>}
              {["admin", "supervisor"].includes(user?.role) && alerts.length > 0 && (
                <button onClick={async () => { try { const { data } = await api.post("/alerts/email"); toast.success(`Resumen enviado a ${data.sent} destinatario(s)`); } catch (e) { toast.error("No se pudo enviar el correo"); } }}
                  data-testid="email-alerts-btn" className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline">
                  <PaperPlaneTilt size={16} weight="fill" /> Enviar por correo
                </button>
              )}
            </div>
            {alerts.length === 0 ? (
              <p className="text-sm text-muted-foreground">Todo en orden. No hay alertas pendientes.</p>
            ) : (
              <div className="space-y-1">
                {alerts.map((a, i) => {
                  const s = SEV[a.severity] || SEV.low;
                  return (
                    <Link key={i} to={a.link} data-testid={`alert-${i}`}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-sm transition-colors duration-200 ${s.bg}`}>
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${s.dot}`} />
                      <span className="text-sm flex-1">{a.message}</span>
                      <ArrowRight size={15} className="text-muted-foreground" />
                    </Link>
                  );
                })}
              </div>
            )}
          </Card>

          <Card className="mt-6 p-6">
            <div className="overline mb-2">Guía rápida</div>
            <div className="font-heading font-bold text-xl mb-3">¿Cómo funciona?</div>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>1. Registra a tus <b className="text-foreground">contratistas</b> y sube el <b className="text-foreground">contrato en PDF</b>: la IA extrae fechas, penalizaciones y obligaciones.</li>
              <li>2. Captura las <b className="text-foreground">estimaciones</b>: el sistema alerta si el cobro excede el avance o el monto del contrato.</li>
              <li>3. Sube <b className="text-foreground">fotos de avance</b> para documentar lo ejecutado.</li>
              <li>4. Registra los <b className="text-foreground">empleados</b> y documenta la <b className="text-foreground">calidad y el avance</b> de cada partida.</li>
            </ul>
          </Card>
        </>
      )}
    </div>
  );
}
